import { Linking, Platform } from "react-native";

import { billingApi } from "@/api";
import { publicConfig } from "@/config/public";
import { useAuthStore } from "@/state/auth-store";
import i18n from "@/i18n";

import { derivePlanPrices } from "./prices";
import type {
  PackPrice,
  PaymentProvider,
  PlanId,
  PlanPrices,
  PurchaseResult,
} from "./types";

/**
 * Native provider (iOS StoreKit + Android Play Billing) via RevenueCat.
 *
 * The SDK is imported dynamically and only ever on iOS/Android: this file is
 * statically imported by `getPaymentProvider()`, which the WEB bundle also
 * walks — a top-level `import Purchases` would drag native SDK code into the
 * web build. Keep it inside `sdk()`.
 *
 * How the pieces line up (all created in the RevenueCat dashboard):
 *   - entitlement `plus` — what the app checks;
 *   - the current offering's ANNUAL / MONTHLY packages — what is bought;
 *   - store products behind those packages (App Store Connect / Play Console).
 *
 * The buyer's plan flips server-side ONLY via the RevenueCat webhook
 * (POST /billing/revenuecat/webhook) — the client never tells the API what it
 * bought, exactly like the Stripe flow. `appUserID` is set to our user id so
 * the webhook can find who to upgrade.
 *
 * Without a platform SDK key in public config these methods report
 * "unavailable" and the UI shows the existing not-in-this-build notice —
 * which is also what every build before the keys exist keeps doing.
 */

/** The RevenueCat entitlement that means "opncue Plus". */
const ENTITLEMENT_ID = "plus";

function platformSdkKey(): string {
  if (Platform.OS === "ios") return publicConfig.revenuecat.iosPublicSdkKey;
  if (Platform.OS === "android") {
    return publicConfig.revenuecat.androidPublicSdkKey;
  }
  return "";
}

type PurchasesModule = typeof import("react-native-purchases").default;

let configuredFor: string | null = null;

/**
 * Load and configure the SDK for the signed-in user, once per user. Returns
 * null when native purchases can't work here (web, no key, no user) — the
 * caller degrades to the "unavailable" result rather than throwing.
 */
async function sdk(): Promise<PurchasesModule | null> {
  const apiKey = platformSdkKey();
  const userId = useAuthStore.getState().user?.id;
  if (!apiKey || !userId) return null;

  const { default: Purchases, LOG_LEVEL } = await import(
    "react-native-purchases"
  );

  if (configuredFor !== userId) {
    // Purchase events must land on OUR user id — the webhook maps
    // `app_user_id` straight back to the account it upgrades.
    Purchases.configure({ apiKey, appUserID: userId });
    if (publicConfig.environment !== "prod") {
      Purchases.setLogLevel(LOG_LEVEL.DEBUG);
    }
    configuredFor = userId;
  }
  return Purchases;
}

type StoreProductIds = { ios: string; android: string };

/** The product id for THIS platform, or null where there is no native store. */
function storeProductId(ids: StoreProductIds | undefined): string | null {
  if (!ids) return null;
  if (Platform.OS === "ios") return ids.ios;
  if (Platform.OS === "android") return ids.android;
  return null;
}

function unavailable(): PurchaseResult {
  return {
    status: "unavailable",
    message: i18n.t("billing.provider.purchasesUnavailable"),
  };
}

export const revenueCatProvider: PaymentProvider = {
  name: "revenuecat",

  /**
   * Both prices from the CURRENT offering, as the store states them.
   *
   * Every field is derived from the two store products — including the annual
   * saving, which is NOT a constant. Apple converted the annual to $69.99 in
   * many storefronts while the monthly is $7.99 or $8.99, so the same pair of
   * products saves 26% in the US and ~35% elsewhere. Quoting one hardcoded
   * percentage would be a false savings claim in most of the world.
   *
   * Returns null when either package is missing (offering not configured, or
   * the products are not yet purchasable) so the caller shows no price at all.
   */
  async prices(): Promise<PlanPrices | null> {
    try {
      const Purchases = await sdk();
      if (!Purchases) return null;

      const offerings = await Purchases.getOfferings();
      const packages = offerings.current?.availablePackages ?? [];
      const monthly = packages.find((p) => p.packageType === "MONTHLY");
      const annual = packages.find((p) => p.packageType === "ANNUAL");
      // The arithmetic lives in ./prices so it can be tested without a store.
      return derivePlanPrices(monthly?.product, annual?.product);
    } catch {
      // No answer from the store is not a licence to invent one.
      return null;
    }
  },

  /**
   * Ask the store to quote every pack we were given, and return only the ones
   * it actually quoted.
   *
   * Packs are consumables and live outside the offering, so they are fetched
   * by product id rather than as packages. The id table is the SERVER'S — it
   * arrives on GET /practice/packs — so there is no second copy here to drift
   * out of step with the store listings.
   *
   * Omitting an unquoted pack is the whole point: a product that is still
   * `MISSING_METADATA`, not yet approved, or missing from this storefront must
   * disappear from the sheet rather than appear at our own number and charge
   * something else at the till.
   *
   * **The category is not optional.** `getProducts` defaults to SUBSCRIPTION,
   * and Play Billing partitions its catalogue by type: asking for a one-time
   * product under the subscription type returns nothing at all, not an error.
   * Every pack then failed the "the store did not quote it" rule above and the
   * sheet hid all three — correct behaviour on a false premise, and silent by
   * construction, since hiding is what an unpriceable pack is SUPPOSED to do.
   */
  async packPrices(
    packs: { id: string; storeProductIds?: StoreProductIds }[],
  ): Promise<PackPrice[] | null> {
    try {
      const Purchases = await sdk();
      if (!Purchases) return [];

      const wanted = new Map<string, string>(); // store product id -> our id
      for (const pack of packs) {
        const productId = storeProductId(pack.storeProductIds);
        if (productId) wanted.set(productId, pack.id);
      }
      if (wanted.size === 0) return [];

      const products = await Purchases.getProducts(
        [...wanted.keys()],
        Purchases.PRODUCT_CATEGORY.NON_SUBSCRIPTION,
      );
      const priced: PackPrice[] = [];
      for (const product of products) {
        const id = wanted.get(product.identifier);
        if (id) priced.push({ id, price: product.priceString });
      }
      return priced;
    } catch {
      // No answer from the store is not a licence to invent one — an empty
      // list hides every pack, which is the safe read of "we cannot price".
      return [];
    }
  },

  async purchasePack(pack: {
    id: string;
    storeProductIds?: StoreProductIds;
  }): Promise<PurchaseResult> {
    try {
      const Purchases = await sdk();
      if (!Purchases) return unavailable();

      const productId = storeProductId(pack.storeProductIds);
      if (!productId) return unavailable();

      // Re-fetch rather than trusting a product cached when the sheet opened:
      // purchaseStoreProduct needs the store's own object, and this is the
      // last point at which "the store will not sell this" is still cheap.
      //
      // NON_SUBSCRIPTION for the same reason as in packPrices: the default is
      // SUBSCRIPTION, and a pack is a consumable. Without it this returned
      // nothing and every purchase reported "unavailable".
      const [product] = await Purchases.getProducts(
        [productId],
        Purchases.PRODUCT_CATEGORY.NON_SUBSCRIPTION,
      );
      if (!product) return unavailable();

      await Purchases.purchaseStoreProduct(product);
      // Deliberately NOT "success": a consumable grants no entitlement, so
      // there is nothing on customerInfo to confirm. The replies appear when
      // the RevenueCat webhook credits the ledger, and the caller waits for
      // the balance to move rather than asserting it already has.
      return { status: "pending" };
    } catch (err) {
      if ((err as { userCancelled?: boolean })?.userCancelled) {
        return { status: "cancelled" };
      }
      return {
        status: "failed",
        message: i18n.t("billing.provider.paymentFailed"),
      };
    }
  },

  async purchase(plan: PlanId): Promise<PurchaseResult> {
    try {
      const Purchases = await sdk();
      if (!Purchases) return unavailable();

      const offerings = await Purchases.getOfferings();
      const packages = offerings.current?.availablePackages ?? [];
      // "plus" without an interval is the legacy alias for the default
      // (annual) plan, same as the Stripe path treats it.
      const wanted = plan === "plus_monthly" ? "MONTHLY" : "ANNUAL";
      const pkg = packages.find((p) => p.packageType === wanted);
      if (!pkg) {
        // Dashboard offering not set up (or store products not approved yet).
        return unavailable();
      }

      const { customerInfo } = await Purchases.purchasePackage(pkg);
      const active = !!customerInfo.entitlements.active[ENTITLEMENT_ID];
      // The store purchase succeeded; the plan flips server-side when the
      // RevenueCat webhook lands (usually seconds). "pending" if the store
      // granted something we don't recognise as Plus.
      return active ? { status: "success" } : { status: "pending" };
    } catch (err) {
      if ((err as { userCancelled?: boolean })?.userCancelled) {
        return { status: "cancelled" };
      }
      return {
        status: "failed",
        message: i18n.t("billing.provider.paymentFailed"),
      };
    }
  },

  async trialInfo(trigger?: string): Promise<{ days: number } | null> {
    try {
      const Purchases = await sdk();
      if (!Purchases) return null;

      // The OFFER is the backend's policy decision on every platform — mode,
      // per-user grant, account age, triggers. Without its yes, no pitch,
      // whatever the store products carry.
      const policy = await billingApi.trial(trigger).catch(() => null);
      if (!policy?.eligible) return null;

      // A store trial is the product's FREE introductory offer. The store is
      // the authority on eligibility (one intro per Apple/Google account);
      // where it can tell us the buyer is ineligible, say nothing rather
      // than promise a trial the purchase sheet won't show.
      const offerings = await Purchases.getOfferings();
      const packages = offerings.current?.availablePackages ?? [];
      const withTrial = packages.find(
        (p) => p.product.introPrice && p.product.introPrice.price === 0,
      );
      const intro = withTrial?.product.introPrice;
      if (!withTrial || !intro) return null;

      const { INTRO_ELIGIBILITY_STATUS } = await import(
        "react-native-purchases"
      );
      const eligibility =
        await Purchases.checkTrialOrIntroductoryPriceEligibility([
          withTrial.product.identifier,
        ]);
      const status = eligibility[withTrial.product.identifier]?.status;
      if (
        status ===
        INTRO_ELIGIBILITY_STATUS.INTRO_ELIGIBILITY_STATUS_INELIGIBLE
      ) {
        return null;
      }

      const perUnit =
        intro.periodUnit === "DAY"
          ? 1
          : intro.periodUnit === "WEEK"
            ? 7
            : intro.periodUnit === "MONTH"
              ? 30
              : 365;
      return { days: intro.periodNumberOfUnits * perUnit };
    } catch {
      return null;
    }
  },

  async manage(): Promise<PurchaseResult> {
    try {
      const Purchases = await sdk();
      if (!Purchases) return unavailable();

      // The store owns the subscription, so managing/cancelling happens in
      // the store's own UI — RevenueCat hands us the deep link.
      const info = await Purchases.getCustomerInfo();
      if (info.managementURL) {
        await Linking.openURL(info.managementURL);
        return { status: "pending" };
      }

      // No management URL means nothing owned on this store account —
      // restoring covers the reinstalled-app / new-device case.
      const restored = await Purchases.restorePurchases();
      return restored.entitlements.active[ENTITLEMENT_ID]
        ? { status: "success" }
        : unavailable();
    } catch {
      return {
        status: "failed",
        message: i18n.t("billing.provider.paymentFailed"),
      };
    }
  },
};
